import {
  Component,
  DestroyRef,
  ElementRef,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
  viewChild
} from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SplitAreaComponent, SplitComponent } from 'angular-split';
import { take } from 'rxjs';

import {
  VisuComponentConfig,
  VisualizationModule
} from '@core/models/data-visualization/data-visualization.model';
import { EntityModificationService } from '@core/services/entity-modification/entity-modification.service';
import { GeoAreaEditSessionService } from '@core/services/geo-area-edit-session/geo-area-edit-session.service';
import { NotificationService } from '@core/services/notification/notification.service';
import {
  entityModified,
  occupationRequested
} from '@core/store/data-visualization/data-visualization.actions';
import {
  selectCalendarVisualization,
  selectIsVisualizationHydrating,
  selectListVisualization,
  selectMapVisualization,
  selectOccupationSitesInView,
  selectOccupationVisualization,
  selectTimelineVisualization
} from '@core/store/data-visualization/data-visualization.selectors';
import { GeoJsonPolygon } from '@core/utils/geo-polygon.utils';
import { isNil } from '@core/utils/shared-utils';

import { CalendarModificationActionsService } from '@features/components/visualization/calendar/calendar-modification-actions.service';
import { CalendarComponent } from '@features/components/visualization/calendar/calendar.component';
import { CalendarEventChangeWithScope } from '@features/components/visualization/calendar/models/calendar.models';
import {
  extractOccurrenceDateSuffix,
  stripOccurrenceDateSuffix
} from '@features/components/visualization/calendar/utils/calendar-drag.utils';
import { ListViewComponent } from '@features/components/visualization/list/list-view/list-view.component';
import { GeoAreaModificationActionsService } from '@features/components/visualization/mapbox/geo-area-modification-actions.service';
import { MapboxComponent } from '@features/components/visualization/mapbox/mapbox.component';
import { GeoAreaModification } from '@features/components/visualization/mapbox/models/galia-area';
import { OccupationComponent } from '@features/components/visualization/occupation/occupation.component';
import { TimelineComponent } from '@features/components/visualization/timeline/timeline.component';

import { SpinnerComponent } from '../../spinner/spinner.component';

@Component({
  selector: 'app-data-visu-container',
  imports: [
    TranslatePipe,
    MapboxComponent,
    ListViewComponent,
    CalendarComponent,
    TimelineComponent,
    OccupationComponent,
    SplitComponent,
    SplitAreaComponent,
    SpinnerComponent
  ],
  templateUrl: './data-visu-container.component.html'
})
export class DataVisuContainerComponent {
  private readonly store = inject(Store);
  private readonly entityModification = inject(EntityModificationService);
  private readonly calendarModifications = inject(CalendarModificationActionsService);
  private readonly notification = inject(NotificationService);
  private readonly translate = inject(TranslateService);
  private readonly geoAreaSession = inject(GeoAreaEditSessionService);
  private readonly areaModifications = inject(GeoAreaModificationActionsService);
  private readonly destroyRef = inject(DestroyRef);

  /** Uid of the session this container opened, so it can drop only its own on teardown. */
  private openedSessionUid: string | null = null;

  constructor() {
    // Occupation reads every route at a site, not the selected ones, so the fetch waits
    // until a panel is open to draw it — and repeats when the selection brings a new site
    // on screen. Sites already resolved cost nothing: the service caches per site.
    effect(() => {
      if (!this.visibleVerticalComponentIds().includes('occupation')) return;
      const siteUids = this.occupationSitesInView();
      if (siteUids.length === 0) return;
      this.store.dispatch(occupationRequested({ siteUids }));
    });

    effect(() => {
      if (!this.isTimelineOpen()) return;
      untracked(() => this.measureTimelineHeight());
    });

    // The container exists while the visu area is collapsed to 0px, so a timeline that is
    // already open then has nothing to measure until the area is shown.
    afterNextRender(() => {
      const resizeObserver = new ResizeObserver(() => this.onContainerResize());
      resizeObserver.observe(this.container().nativeElement);
      this.destroyRef.onDestroy(() => resizeObserver.disconnect());
    });

    // The session outlives this component: it is application-wide, and the map that would
    // let the user finish it goes away with the container. Left running, it re-opens on the
    // next map with a commit that writes the zone the user walked away from.
    this.destroyRef.onDestroy(() => {
      if (
        this.openedSessionUid !== null &&
        this.geoAreaSession.activeUid() === this.openedSessionUid
      ) {
        this.geoAreaSession.cancel();
      }
    });
  }

  canFilterData = input<boolean>(false);
  /**
   * Whether an entity can be edited straight from this visualization. Off by default, and
   * left off on the edit page, where the form owns the value the map is showing.
   */
  inPlaceEditingEnabled = input<boolean>(false);
  visibleComponents = input.required<VisuComponentConfig[]>();

  calendarVisualization = this.store.selectSignal(selectCalendarVisualization);
  timelineVisualization = this.store.selectSignal(selectTimelineVisualization);
  listVisualization = this.store.selectSignal(selectListVisualization);
  mapVisualization = this.store.selectSignal(selectMapVisualization);
  occupationVisualization = this.store.selectSignal(selectOccupationVisualization);
  private readonly occupationSitesInView = this.store.selectSignal(
    selectOccupationSitesInView
  );
  isHydrating = this.store.selectSignal(selectIsVisualizationHydrating);

  visibleHorizontalComponentIds = computed(() =>
    this.visibleComponents()
      .filter((comp) => comp.layout === 'horizontal')
      .map((comp) => comp.id)
  );
  visibleVerticalComponentIds = computed(() =>
    this.visibleComponents()
      .filter((comp) => comp.layout === 'vertical')
      .map((comp) => comp.id)
  );
  private readonly isTimelineOpen = computed(
    () => this.visibleHorizontalComponentIds().length > 0
  );

  private readonly container = viewChild.required<ElementRef<HTMLElement>>('container');
  /**
   * Pixel height of the timeline, so resizing the visu from above only resizes the row of
   * panels over it. Measured once when the timeline opens; afterwards only the user's drag
   * changes it.
   */
  protected readonly timelineHeight = signal<number | '*'>('*');

  private measureTimelineHeight(): void {
    const containerHeight = this.container().nativeElement.clientHeight;
    if (containerHeight > 0) {
      this.timelineHeight.set(Math.round(containerHeight / 2));
    }
  }

  private onContainerResize(): void {
    if (this.timelineHeight() === '*' && this.isTimelineOpen()) {
      this.measureTimelineHeight();
    }
  }

  protected getMinSizeForComponent(id: VisualizationModule): number {
    const comp = this.visibleComponents().find((c) => c.id === id);
    if (!comp) return 0;

    return comp.minSize ?? 0;
  }

  /**
   * Opens the clicked boundary for reshaping. Committing writes the row directly, the same
   * way a dragged calendar event does — there is no form in front of this map to hold the
   * value, so the session's commit is the save.
   */
  protected onAreaSelected(uid: string): void {
    const modification = this.areaModifications.getModification(uid);
    if (!modification || this.geoAreaSession.isActive()) return;

    this.openedSessionUid = uid;
    this.geoAreaSession.begin({
      uid,
      geometry: modification.geometry,
      label: modification.name,
      // The row's boundary is what makes it a GeoJson zone; clearing is a step towards
      // drawing another, not a way to save a zone with none.
      allowEmpty: false,
      onCommit: (geometry) => this.saveAreaGeometry(modification, geometry)
    });
  }

  private saveAreaGeometry(
    modification: GeoAreaModification,
    geometry: GeoJsonPolygon | null
  ): void {
    if (!geometry) return;

    this.entityModification
      .saveEntityData$(modification.entityType, modification.entityTypeVersion, () =>
        modification.withGeometry(geometry)
      )
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.store.dispatch(entityModified({ entityType: modification.entityType }));
          this.notification.success(
            this.translate.instant('features.geographic_area.editor.saved_title'),
            this.translate.instant('features.geographic_area.editor.saved_message', {
              name: modification.name
            })
          );
        },
        error: () => {
          this.notification.error(
            this.translate.instant('features.geographic_area.editor.save_failed_title'),
            this.translate.instant('features.geographic_area.editor.save_failed_message', {
              name: modification.name
            })
          );
        }
      });
  }

  protected onCalendarEventChange(change: CalendarEventChangeWithScope): void {
    const baseUid = stripOccurrenceDateSuffix(change.uid);
    const mod = this.calendarModifications.getModification(baseUid);
    if (!mod) return;

    let dataFactory: (() => Record<string, unknown>) | undefined;
    if (change.scope === 'exception') {
      const occurrenceDate = extractOccurrenceDateSuffix(change.uid);
      if (occurrenceDate && mod.withException) {
        dataFactory = () => mod.withException!(occurrenceDate, change.start, change.end);
      }
    } else if (change.scope === 'next') {
      const occurrenceDate = extractOccurrenceDateSuffix(change.uid);
      if (occurrenceDate && mod.withFollowing) {
        dataFactory = () => mod.withFollowing!(occurrenceDate, change.start, change.end);
      }
    } else {
      dataFactory = () => mod.withNewTimes(change.start, change.end);
    }
    if (!dataFactory) return;

    this.entityModification
      .saveEntityData$(mod.entityType, mod.entityTypeVersion, dataFactory)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.store.dispatch(entityModified({ entityType: mod.entityType }));
          if (!isNil(mod.refreshEntityType)) {
            this.store.dispatch(entityModified({ entityType: mod.refreshEntityType }));
          }
          this.notification.success(
            this.translate.instant('common.calendar.drag_update_success_title'),
            this.translate.instant('common.calendar.drag_update_success_message')
          );
        },
        error: () => {
          this.notification.error(
            this.translate.instant('common.calendar.drag_update_error_title'),
            this.translate.instant('common.calendar.drag_update_error_message')
          );
        }
      });
  }
}
